import { createContext, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./index.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import App from "./App.jsx";
import "react-toastify/dist/ReactToastify.css";
import "antd/dist/reset.css"; // AntD v5
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./components/context/AuthContext";

/**
 * Global API Interceptors (Fetch & XHR)
 * Implements mandatory security headers (x-request-id, x-timestamp) for all network calls.
 * This ensures that Axios, Fetch, and legacy XHR calls across the codebase obey the
 * system's non-negotiable anti-replay and integrity rules.
 */

// 1. Fetch Interceptor
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  let [resource, config] = args;
  const url = typeof resource === "string" ? resource : resource?.url || "";

  // Intercept if it's a request to our API surface
  if (
    url.includes("/api/") ||
    url.includes("localhost:3001") ||
    url.includes("pixelmoonstore.in") ||
    url.includes("pixelmoon.ddns.net") ||
    url.includes("vercel.app")
  ) {
    config = config || {};
    const headers = config.headers || {};
    const requestId =
      window.crypto?.randomUUID?.() ||
      Date.now().toString(36) + Math.random().toString(36).substring(2);
    const timestamp = Date.now().toString();
    const fingerprint = localStorage.getItem("x-device-fingerprint");
    const getCookie = (name) => {
      try {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(";").shift();
      } catch (e) {
        return null;
      }
      return null;
    };
    const cookieSecret = getCookie("x-csrf-token-secret");
    const csrfToken =
      cookieSecret || localStorage.getItem("x-csrf-token-fallback");

    if (headers instanceof Headers) {
      if (!headers.has("x-request-id")) headers.set("x-request-id", requestId);
      if (!headers.has("x-timestamp")) headers.set("x-timestamp", timestamp);
      if (fingerprint && !headers.has("x-device-fingerprint"))
        headers.set("x-device-fingerprint", fingerprint);
      if (csrfToken && !headers.has("x-csrf-token"))
        headers.set("x-csrf-token", csrfToken);
    } else {
      config.headers = {
        "x-request-id": requestId,
        "x-timestamp": timestamp,
        ...(fingerprint ? { "x-device-fingerprint": fingerprint } : {}),
        ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
        ...headers,
      };
    }
  }

  const response = await originalFetch(resource, config);

  // Zero Trust: Capture rotated CSRF token from server headers
  const csrfHeader = response.headers.get("x-csrf-token");
  if (csrfHeader) {
    localStorage.setItem("x-csrf-token-fallback", csrfHeader);
  }

  return response;
};

// 2. XMLHttpRequest Interceptor (Covers Axios)
const originalOpen = XMLHttpRequest.prototype.open;
const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

// Hook setRequestHeader to track applied headers and prevent duplicates
XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
  this._appliedHeaders = this._appliedHeaders || {};
  this._appliedHeaders[name.toLowerCase()] = value;
  return originalSetRequestHeader.apply(this, [name, value]);
};

XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  this._url = url;

  // Zero Trust: Capture rotated CSRF token from XHR responses
  this.addEventListener("readystatechange", function () {
    if (this.readyState === 4) {
      const csrfHeader = this.getResponseHeader("x-csrf-token");
      if (csrfHeader) {
        localStorage.setItem("x-csrf-token-fallback", csrfHeader);
      }
    }
  });

  return originalOpen.apply(this, [method, url, ...rest]);
};

const originalSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.send = function (...args) {
  const url = this._url || "";
  if (
    url.includes("/api/") ||
    url.includes("localhost:3001") ||
    url.includes("pixelmoonstore.in") ||
    url.includes("pixelmoon.ddns.net") ||
    url.includes("vercel.app")
  ) {
    try {
      const headers = this._appliedHeaders || {};

      // Anti-Replay Headers: Only set if not already present
      if (!headers["x-request-id"]) {
        const requestId =
          window.crypto?.randomUUID?.() ||
          Date.now().toString(36) + Math.random().toString(36).substring(2);
        this.setRequestHeader("x-request-id", requestId);
      }
      if (!headers["x-timestamp"]) {
        this.setRequestHeader("x-timestamp", Date.now().toString());
      }

      // Zero Trust: Attach fingerprint if not present
      if (!headers["x-device-fingerprint"]) {
        const fingerprint = localStorage.getItem("x-device-fingerprint");
        if (fingerprint)
          this.setRequestHeader("x-device-fingerprint", fingerprint);
      }

      // CSRF Protection: Only attach if not already present (e.g. by hardened axios client)
      if (!headers["x-csrf-token"]) {
        const getCookie = (name) => {
          try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(";").shift();
          } catch (e) {
            return null;
          }
          return null;
        };
        const cookieSecret = getCookie("x-csrf-token-secret");
        const csrfToken =
          cookieSecret || localStorage.getItem("x-csrf-token-fallback");
        if (csrfToken) this.setRequestHeader("x-csrf-token", csrfToken);
      }
    } catch (err) {
      // Silent fail if headers cannot be set (e.g. before open or already sent)
    }
  }
  return originalSend.apply(this, args);
};

export const Context = createContext({
  isAuthenticated: false,
  setIsAuthenticated: () => {},
  user: null,
  setUser: () => {},
});

const AppWrapper = () => {
  return (
    <GoogleOAuthProvider clientId="641186881779-oudgv17vmk7emqnijd8fb1t529r0n55l.apps.googleusercontent.com">
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
};

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

// Check if we already have a React root
const existingRoot = rootElement._reactRootContainer;

if (existingRoot) {
  // If we already have a root, just update it
  const root = existingRoot._internalRoot;
  root.render(
    <StrictMode>
      <AppWrapper />
    </StrictMode>,
  );
} else {
  // Otherwise create a new root
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <AppWrapper />
    </StrictMode>,
  );
}
